<template>
  <div class="trend-kline-container">
    <div class="filter-bar">
      <el-form :inline="true" size="small">
        <el-form-item label="范围">
          <el-radio-group v-model="rangeDays" @change="fetchData()">
            <el-radio-button :label="1">最近一天</el-radio-button>
            <el-radio-button :label="3">最近三天</el-radio-button>
            <el-radio-button :label="7">最近七天</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchData()" :loading="loading">查询</el-button>
        </el-form-item>
        <el-form-item>
          <span class="data-info" v-if="result">
            共 <b>{{ result.totalPeriods }}</b> 期 | 最新期号: <b>{{ result.latestIssue }}</b>
            <template v-if="nextRefreshText"> | 下次刷新: <b>{{ nextRefreshText }}</b></template>
          </span>
        </el-form-item>
      </el-form>
    </div>

    <div v-if="loading" class="loading-mask">
      <i class="el-icon-loading" /> 正在分析形态趋势...
    </div>

    <div v-else-if="errorMsg" class="error-mask">
      <p>{{ errorMsg }}</p>
      <el-button @click="fetchData()">重试</el-button>
    </div>

    <div v-else-if="!result" class="empty-hint">
      点击「查询」加载最近288期形态趋势数据
    </div>

    <div v-else class="analysis-area">
      <div class="cost-strip">
        <span v-for="group in focusGroups" :key="group.key">
          {{ group.shortName }}: {{ group.notes }}注 x {{ fmtMoney(unitStake) }} = {{ fmtMoney(group.cost) }}，中净 {{ fmtMoney(group.hitProfit) }}
        </span>
      </div>

      <div class="decision-grid">
        <div v-for="combo in comboAnalyses" :key="combo.key" class="decision-panel">
          <div class="panel-title">{{ combo.shortName }}</div>
          <div class="decision-row decision-head">
            <span>口径</span>
            <span>基准</span>
            <span>近30</span>
            <span>遗漏</span>
            <span>
              <el-tooltip
                content="历史遗漏的80%-95%分位区间。当前遗漏进入该区间说明已经偏长，接近或超过上沿时追号失败风险更高。"
                placement="top"
              >
                <span class="help-label">高危区</span>
              </el-tooltip>
            </span>
            <span>趋势</span>
            <span>判断</span>
            <span>追号</span>
          </div>
          <div v-for="group in focusGroups" :key="combo.key + group.key" class="decision-row">
            <span>{{ group.shortName }}</span>
            <span>{{ fmtPct(group.baseline) }}</span>
            <span :class="deltaClass(combo.groups[group.key].rolling30, group.baseline)">
              {{ fmtPct(combo.groups[group.key].rolling30) }}
            </span>
            <span :class="missClass(combo.groups[group.key].currentMiss, group.expectedMiss)">
              {{ combo.groups[group.key].currentMiss }}
            </span>
            <span>{{ combo.groups[group.key].p80Miss }}-{{ combo.groups[group.key].p95Miss }}</span>
            <span>
              <b :class="'trend ' + combo.groups[group.key].trend.type">
                {{ combo.groups[group.key].trend.label }}
              </b>
            </span>
            <span>
              <b :class="'signal ' + combo.groups[group.key].signal.type">
                {{ combo.groups[group.key].signal.label }}
              </b>
            </span>
            <span>
              <el-tooltip :content="combo.groups[group.key].advice.reason" placement="top">
                <b :class="'advice ' + combo.groups[group.key].advice.type">
                  {{ combo.groups[group.key].advice.label }}
                </b>
              </el-tooltip>
            </span>
          </div>
        </div>
      </div>

      <div class="chart-panel">
        <div class="chart-title">最近命中分布</div>
        <div ref="signalChart" class="chart-box signal-chart"></div>
      </div>

      <div class="focus-charts">
        <div v-for="group in focusGroups" :key="group.key" class="chart-panel">
          <div class="chart-title">
            {{ group.name }}
            <span>最近遗漏趋势 | 基准期望遗漏 {{ group.expectedMiss.toFixed(1) }}</span>
          </div>
          <div :ref="'focusChart_' + group.key" class="chart-box"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { trendAnalysis } from '@/api/trendAnalysis'

const UNIT_STAKE = 0.002
const HIT_PAYOUT = 1.98

const COMBOS = [
  { key: 'front', name: '前三位(万千百)', shortName: '前三', field: 'type012' },
  { key: 'middle', name: '中三位(千百十)', shortName: '中三', field: 'type123' },
  { key: 'back', name: '后三位(百十个)', shortName: '后三', field: 'type234' }
]

const FOCUS_GROUPS = [
  { key: 'pair', name: '对子', shortName: '对子', types: ['对子'], baseline: 0.27, notes: 270 },
  { key: 'pairLeopard', name: '对子+豹子', shortName: '对+豹', types: ['对子', '豹子'], baseline: 0.28, notes: 280 },
  { key: 'pairLeopardStraight', name: '对子+豹子+顺子', shortName: '对+豹+顺', types: ['对子', '豹子', '顺子'], baseline: 0.34, notes: 340 }
].map(group => ({
  ...group,
  expectedMiss: (1 - group.baseline) / group.baseline,
  cost: Number((group.notes * UNIT_STAKE).toFixed(3)),
  hitProfit: Number((HIT_PAYOUT - group.notes * UNIT_STAKE).toFixed(3)),
  expectedProfit: Number((group.baseline * HIT_PAYOUT - group.notes * UNIT_STAKE).toFixed(4))
}))

const COMBO_COLORS = {
  front: '#c23531',
  middle: '#2f90b9',
  back: '#67a965'
}

const TYPE_COLORS = {
  豹子: '#7b3f98',
  对子: '#c23531',
  顺子: '#2f90b9'
}

export default {
  name: 'TrendKLine',
  data () {
    return {
      rangeDays: 1,
      loading: false,
      errorMsg: '',
      result: null,
      comboAnalyses: [],
      focusGroups: FOCUS_GROUPS,
      charts: [],
      refreshTimer: null,
      nextRefreshAt: null,
      unitStake: UNIT_STAKE
    }
  },
  mounted () {
    this.fetchData()
    this.scheduleNextRefresh()
  },
  beforeDestroy () {
    this.clearRefreshTimer()
    this.disposeCharts()
  },
  computed: {
    nextRefreshText () {
      if (!this.nextRefreshAt) return ''
      const d = this.nextRefreshAt
      return this.padTime(d.getHours()) + ':' + this.padTime(d.getMinutes()) + ':' + this.padTime(d.getSeconds())
    }
  },
  methods: {
    async fetchData (silent = false) {
      if (this.loading) return
      this.loading = true
      this.errorMsg = ''
      this.disposeCharts()
      try {
        const res = await trendAnalysis({
          limit: this.rangeDays * 288
        })
        const result = this.normalizeTrendResult(res)
        if (!result || !Array.isArray(result.kLineData)) {
          this.errorMsg = '形态趋势接口返回格式异常，请检查登录状态和接口响应'
          this.result = null
          return
        }
        if (result.kLineData.length === 0) {
          this.errorMsg = '未获取到有效形态数据，请检查期号范围或开奖号码字段'
          this.result = null
          return
        }
        this.result = result
        this.comboAnalyses = this.buildComboAnalyses(result.kLineData)
        this.$nextTick(() => {
          try {
            this.renderCharts()
          } catch (e) {
            console.error('Chart render error:', e)
            this.errorMsg = '图表渲染失败: ' + e.message
          }
        })
      } catch (e) {
        console.error('Trend analysis error:', e)
        this.errorMsg = (e.response && e.response.data && e.response.data.message) || e.message || '请求失败'
      } finally {
        this.loading = false
        if (!silent) this.scheduleNextRefresh()
      }
    },

    scheduleNextRefresh () {
      this.clearRefreshTimer()
      const now = new Date()
      const next = new Date(now.getTime())
      next.setMilliseconds(0)
      next.setSeconds(30)
      const minutes = now.getMinutes()
      const nextMinute = Math.ceil((minutes + (now.getSeconds() >= 30 ? 1 : 0)) / 5) * 5
      if (nextMinute >= 60) {
        next.setHours(now.getHours() + 1)
        next.setMinutes(0)
      } else {
        next.setMinutes(nextMinute)
      }
      if (next <= now) {
        next.setMinutes(next.getMinutes() + 5)
      }
      this.nextRefreshAt = next
      this.refreshTimer = setTimeout(async () => {
        await this.fetchData(true)
        this.scheduleNextRefresh()
      }, next.getTime() - now.getTime())
    },

    clearRefreshTimer () {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    normalizeTrendResult (res) {
      const result = res && res.data ? res.data : res
      if (!result) return null
      if (!result.kLineData && result.klineData) result.kLineData = result.klineData
      if (!result.kLineData && result.KLineData) result.kLineData = result.KLineData
      return result
    },

    resetFilter () {
      this.rangeDays = 1
      this.result = null
      this.errorMsg = ''
      this.comboAnalyses = []
      this.disposeCharts()
      this.fetchData()
    },

    buildComboAnalyses (data) {
      const issues = data.map(d => d.issue)
      return COMBOS.map(combo => {
        const types = data.map(d => d[combo.field] || '')
        const groups = {}
        FOCUS_GROUPS.forEach(group => {
          const hits = types.map(type => group.types.indexOf(type) > -1)
          groups[group.key] = this.analyzeHits(hits, issues, group)
        })
        return { ...combo, types, groups }
      })
    },

    analyzeHits (hits, issues, group) {
      const count = hits.filter(Boolean).length
      const rolling15 = this.lastRate(hits, 15)
      const rolling30 = this.lastRate(hits, 30)
      const rolling60 = this.lastRate(hits, 60)
      const currentMiss = this.currentMiss(hits)
      const missStats = this.missStats(hits)
      const missSeries = this.missSeries(hits)
      const trend = this.buildTrend(rolling15, rolling30, rolling60, currentMiss, group, missStats)
      const signal = this.buildSignal(rolling15, rolling30, rolling60, currentMiss, group, missStats, trend)
      return {
        count,
        rate: hits.length > 0 ? count / hits.length : 0,
        rolling15,
        rolling30,
        rolling60,
        rollingSeries30: this.rollingSeries(hits, 30),
        missSeries,
        avgMiss: this.average(missSeries),
        currentMiss,
        maxMiss: missStats.max,
        p80Miss: missStats.p80,
        p90Miss: missStats.p90,
        p95Miss: missStats.p95,
        trend,
        signal,
        advice: this.buildAdvice(trend, signal, currentMiss, group, missStats),
        issues
      }
    },

    buildAdvice (trend, signal, currentMiss, group, missStats) {
      const p80Line = Math.max(missStats.p80, Math.ceil(group.expectedMiss * 2.5))
      const p95Line = Math.max(missStats.p95, Math.ceil(group.expectedMiss * 4))
      const money = '单期成本' + this.fmtMoney(group.cost) + '，命中净利' + this.fmtMoney(group.hitProfit)
      if (signal.type === 'miss' || currentMiss >= p95Line) {
        return { type: 'stop', label: '停追', reason: money + '。当前遗漏已到大遗漏区，继续加追容易被拉长。' }
      }
      if (trend.type === 'hot-cold' || trend.type === 'cold') {
        return { type: 'wait', label: '等回落', reason: money + '。趋势转冷或偏冷，先等遗漏回到均值附近再考虑。' }
      }
      if (trend.type === 'cold-hot') {
        return { type: 'light', label: '轻追1-2', reason: money + '。冷转热但仍要限期，连续不中且遗漏进入高危区就停。' }
      }
      if (signal.type === 'dense' || trend.type === 'hot') {
        return { type: 'follow', label: '追1期', reason: money + '。短线偏密集，适合只跟当前热度，不建议拉长倍投。' }
      }
      if (trend.type === 'stable' && currentMiss <= p80Line) {
        return { type: 'light', label: '均值轻追', reason: money + '。当前在平稳区，遗漏未进高危区，可以低倍短追。' }
      }
      return { type: 'wait', label: '观望', reason: money + '。趋势不清晰，等遗漏方向或命中密度更明确。' }
    },

    buildSignal (rolling15, rolling30, rolling60, currentMiss, group, missStats, trend) {
      const base = group.baseline
      const p80Line = Math.max(missStats.p80, Math.ceil(group.expectedMiss * 2.5))
      const p95Line = Math.max(missStats.p95, Math.ceil(group.expectedMiss * 4))
      if (currentMiss >= p95Line && rolling30 <= base + 0.03) {
        return { type: 'miss', label: '大遗漏' }
      }
      if (rolling15 >= base + 0.14 && rolling30 >= base + 0.08 && currentMiss <= group.expectedMiss * 1.5) {
        return { type: 'dense', label: '密集' }
      }
      if (currentMiss >= p80Line || trend.type === 'hot-cold') {
        return { type: 'watch-miss', label: '偏遗漏' }
      }
      if (trend.type === 'cold-hot' || rolling30 >= base + 0.06 || rolling15 >= base + 0.1) {
        return { type: 'watch-dense', label: '偏密集' }
      }
      return { type: 'normal', label: '正常' }
    },

    buildTrend (rolling15, rolling30, rolling60, currentMiss, group, missStats) {
      const base = group.baseline
      const p80Line = Math.max(missStats.p80, Math.ceil(group.expectedMiss * 2.5))
      const p90Line = Math.max(missStats.p90, Math.ceil(group.expectedMiss * 3.2))
      if (rolling15 >= rolling30 + 0.08 && rolling30 >= rolling60 - 0.02 && rolling15 >= base + 0.06) {
        return { type: 'cold-hot', label: '冷转热' }
      }
      if (rolling15 <= rolling30 - 0.08 && rolling30 <= rolling60 + 0.02 && currentMiss >= p80Line) {
        return { type: 'hot-cold', label: '热转冷' }
      }
      if (Math.abs(rolling30 - base) <= 0.04 && currentMiss < p80Line) {
        return { type: 'stable', label: '平稳' }
      }
      if (currentMiss >= p90Line) {
        return { type: 'cold', label: '偏冷' }
      }
      if (rolling30 >= base + 0.08) {
        return { type: 'hot', label: '偏热' }
      }
      return { type: 'normal', label: '震荡' }
    },

    lastRate (hits, windowSize) {
      if (hits.length === 0) return 0
      const start = Math.max(0, hits.length - windowSize)
      const part = hits.slice(start)
      return part.filter(Boolean).length / part.length
    },

    rollingSeries (hits, windowSize) {
      return hits.map((_, idx) => {
        const start = Math.max(0, idx - windowSize + 1)
        const part = hits.slice(start, idx + 1)
        return Number((part.filter(Boolean).length / part.length * 100).toFixed(2))
      })
    },

    missSeries (hits) {
      let miss = 0
      return hits.map(hit => {
        if (hit) {
          miss = 0
          return 0
        }
        miss++
        return miss
      })
    },

    currentMiss (hits) {
      let count = 0
      for (let i = hits.length - 1; i >= 0; i--) {
        if (hits[i]) break
        count++
      }
      return count
    },

    missStats (hits) {
      const misses = []
      let miss = 0
      hits.forEach(hit => {
        if (hit) {
          misses.push(miss)
          miss = 0
        } else {
          miss++
        }
      })
      misses.push(miss)
      const sorted = misses.filter(v => v > 0).sort((a, b) => a - b)
      if (sorted.length === 0) {
        return { max: 0, p80: 0, p90: 0, p95: 0 }
      }
      return {
        max: sorted[sorted.length - 1],
        p80: this.quantile(sorted, 0.8),
        p90: this.quantile(sorted, 0.9),
        p95: this.quantile(sorted, 0.95)
      }
    },

    quantile (sorted, q) {
      const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(sorted.length * q) - 1))
      return sorted[idx]
    },

    async renderCharts () {
      const echarts = await import('echarts')
      const data = this.result.kLineData
      const issues = data.map(d => d.issue)

      this.renderSignalChart(echarts, data, issues)
      FOCUS_GROUPS.forEach(group => this.renderFocusChart(echarts, group, issues))

      window.addEventListener('resize', this.handleResize)
    },

    renderSignalChart (echarts, data, issues) {
      const el = this.getRefElement('signalChart')
      if (!el) return
      const chart = echarts.init(el)
      const scatterData = []
      data.forEach((item, idx) => {
        COMBOS.forEach((combo, row) => {
          const type = item[combo.field]
          if (TYPE_COLORS[type]) {
            scatterData.push({
              name: type,
              value: [idx, row],
              symbolSize: type === '豹子' ? 10 : 7,
              itemStyle: { color: TYPE_COLORS[type] }
            })
          }
        })
      })
      chart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: p => {
            const idx = p.value[0]
            const item = data[idx]
            return '<b>' + issues[idx] + '</b><br/>' +
              '前三:' + item.type012 + '<br/>' +
              '中三:' + item.type123 + '<br/>' +
              '后三:' + item.type234
          }
        },
        grid: { left: 40, right: 14, top: 8, bottom: 22 },
        xAxis: { type: 'category', data: issues, axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
        yAxis: {
          type: 'value',
          min: -0.5,
          max: 2.5,
          interval: 1,
          axisLabel: { formatter: v => ['前三', '中三', '后三'][v] || '', fontSize: 11 },
          splitLine: { show: false }
        },
        dataZoom: [{ type: 'slider', xAxisIndex: 0, height: 12, bottom: 2, start: 0, end: 100 }],
        series: [{ type: 'scatter', data: scatterData }]
      })
      this.charts.push(chart)
    },

    renderFocusChart (echarts, group, issues) {
      const el = this.getRefElement('focusChart_' + group.key)
      if (!el) return
      const chart = echarts.init(el)
      const missSeries = this.comboAnalyses.map(combo => ({
        name: combo.shortName + '遗漏',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 3,
        data: combo.groups[group.key].missSeries,
        lineStyle: { width: 2 },
        itemStyle: { color: COMBO_COLORS[combo.key] }
      }))
      const avgSeries = this.comboAnalyses.map(combo => {
        const avgMiss = combo.groups[group.key].avgMiss
        return {
          name: combo.shortName + '均值',
          type: 'line',
          symbol: 'none',
          silent: true,
          data: issues.map(() => avgMiss),
          lineStyle: { width: 1, type: 'dashed', opacity: 0.7 },
          itemStyle: { color: COMBO_COLORS[combo.key] }
        }
      })
      const riskSeries = this.comboAnalyses.map(combo => ({
        name: combo.shortName + '高危线',
        type: 'line',
        symbol: 'none',
        silent: true,
        data: [],
        markLine: {
          symbol: 'none',
          silent: true,
          lineStyle: { color: COMBO_COLORS[combo.key], type: 'dashed', opacity: 0.5 },
          label: { formatter: combo.shortName + '高危' },
          data: [{ yAxis: combo.groups[group.key].p90Miss }]
        }
      }))
      chart.setOption({
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const issue = params[0] && params[0].axisValue
            const lines = ['<b>' + issue + '</b>']
            params
              .filter(p => p.seriesName.indexOf('高危线') === -1)
              .forEach(p => {
                lines.push(p.marker + p.seriesName + ': ' + Number(p.value).toFixed(1))
              })
            return lines.join('<br/>')
          }
        },
        legend: { top: 0, data: [...missSeries, ...avgSeries].map(x => x.name) },
        grid: { left: 44, right: 44, top: 34, bottom: 28 },
        xAxis: { type: 'category', data: issues, axisLabel: { show: false }, axisTick: { show: false } },
        yAxis: { type: 'value', name: '遗漏', minInterval: 1, splitLine: { show: true } },
        dataZoom: [{ type: 'inside' }],
        series: [...missSeries, ...avgSeries, ...riskSeries]
      })
      this.charts.push(chart)
    },

    getRefElement (refName) {
      const ref = this.$refs[refName]
      return Array.isArray(ref) ? ref[0] : ref
    },

    disposeCharts () {
      window.removeEventListener('resize', this.handleResize)
      this.charts.forEach(c => { try { c.dispose() } catch (e) { /* ignore */ } })
      this.charts = []
    },

    handleResize () {
      this.charts.forEach(c => { try { c.resize() } catch (e) { /* ignore */ } })
    },

    fmtPct (v) {
      return (v * 100).toFixed(1) + '%'
    },

    fmtMoney (v) {
      return Number(v).toFixed(3)
    },

    padTime (v) {
      return v < 10 ? '0' + v : String(v)
    },

    average (values) {
      if (!values.length) return 0
      const total = values.reduce((sum, v) => sum + v, 0)
      return Number((total / values.length).toFixed(2))
    },

    deltaClass (current, base) {
      if (current >= base + 0.08) return 'dense-text'
      if (current <= base - 0.08) return 'miss-text'
      return ''
    },

    missClass (miss, expectedMiss) {
      if (miss >= expectedMiss * 4) return 'miss-text'
      if (miss >= expectedMiss * 2.5) return 'watch-text'
      return ''
    }
  }
}
</script>

<style scoped>
.trend-kline-container { padding: 12px; background: #f5f5f5; min-height: 100vh; }
.filter-bar { background: #fff; padding: 12px 16px; border-radius: 4px; margin-bottom: 12px; }
.data-info { color: #666; font-size: 12px; }
.analysis-area { display: flex; flex-direction: column; gap: 12px; }
.cost-strip { display: flex; flex-wrap: wrap; gap: 10px; color: #606266; font-size: 12px; }
.cost-strip span { background: #fff; border-radius: 4px; padding: 7px 10px; }
.decision-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.decision-panel, .chart-panel { background: #fff; border-radius: 4px; padding: 12px 14px; }
.panel-title, .chart-title { font-size: 13px; font-weight: bold; color: #303133; margin-bottom: 8px; }
.chart-title span { color: #909399; font-weight: normal; font-size: 12px; margin-left: 8px; }
.decision-row { display: grid; grid-template-columns: 58px 46px 46px 38px 50px 52px 52px 62px; gap: 6px; align-items: center; min-height: 28px; font-size: 12px; color: #303133; }
.decision-head { color: #909399; border-bottom: 1px solid #ebeef5; margin-bottom: 4px; }
.signal { display: inline-block; min-width: 48px; text-align: center; border-radius: 3px; padding: 2px 4px; color: #fff; font-size: 12px; }
.signal.dense { background: #c23531; }
.signal.watch-dense { background: #e6902e; }
.signal.miss { background: #2f90b9; }
.signal.watch-miss { background: #61a0a8; }
.signal.normal { background: #909399; }
.trend { display: inline-block; min-width: 48px; text-align: center; border-radius: 3px; padding: 2px 4px; color: #fff; font-size: 12px; }
.trend.cold-hot, .trend.hot { background: #c23531; }
.trend.hot-cold, .trend.cold { background: #2f90b9; }
.trend.stable { background: #67a965; }
.trend.normal { background: #909399; }
.advice { display: inline-block; min-width: 56px; text-align: center; border-radius: 3px; padding: 2px 4px; color: #fff; font-size: 12px; }
.advice.follow { background: #c23531; }
.advice.light { background: #e6902e; }
.advice.wait { background: #909399; }
.advice.stop { background: #2f90b9; }
.dense-text { color: #c23531; font-weight: bold; }
.miss-text { color: #2f90b9; font-weight: bold; }
.watch-text { color: #e6902e; font-weight: bold; }
.focus-charts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.chart-box { width: 100%; height: 250px; }
.signal-chart { height: 120px; }
.loading-mask, .error-mask, .empty-hint { text-align: center; padding: 80px; color: #999; font-size: 14px; }
.error-mask { color: #c23531; }
@media (max-width: 1400px) {
  .decision-grid, .focus-charts { grid-template-columns: 1fr; }
}
</style>
